import React, { useRef, useEffect } from "react";
import { motion } from "framer-motion";
import { Volume2, Loader2, Play } from "lucide-react";
import { useTranslate } from "../context/TranslateContext";
import { useAudio } from "../context/AudioContext";

let _seq = 0;

// "Listen" (start) control. Delegates all playback to the global AudioProvider, so the
// floating GlobalAudioBar can pause/stop the audio from anywhere on any page.
export const ListenButton = ({ text, label = "Listen", testId = "listen-btn", compact = false, sourceId, sourceLabel }) => {
  const ctx = useTranslate();
  const audio = useAudio();
  const idRef = useRef(`${testId}-${(_seq += 1)}`);
  const myId = sourceId || idRef.current;

  const spokenText = ctx ? ctx.t(text) : text;
  const langName = ctx?.lang?.name || "English";

  const isActive = audio?.source?.id === myId;
  const state = isActive ? audio.state : "idle";

  // Stop this button's audio if the display language changes.
  useEffect(() => {
    if (isActive) audio.stop();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ctx?.lang?.code]);

  const onClick = () => {
    if (!audio) return;
    if (isActive && state === "loading") return; // avoid restarting synthesis mid-load
    if (isActive && state === "playing") { audio.pause(); return; }
    if (isActive && state === "paused") { audio.resume(); return; }
    audio.playText(spokenText, langName, { id: myId, label: sourceLabel || label });
  };

  return (
    <button
      onClick={onClick}
      data-testid={testId}
      aria-label={`${label} in ${langName}`}
      className={`group relative inline-flex items-center gap-2 rounded-full border border-edge bg-surface/70 ${compact ? "px-3 py-1.5 text-xs" : "px-4 py-2 text-sm"} text-cream backdrop-blur-md transition-colors duration-300 hover:border-gold hover:text-gold`}
    >
      {state === "loading" ? (
        <Loader2 className="h-4 w-4 animate-spin text-gold" />
      ) : state === "playing" ? (
        <span className="flex h-4 items-end gap-[2px] text-gold">
          {[0, 1, 2, 3].map((i) => (
            <motion.span key={i} className="eq-bar"
              animate={{ scaleY: [0.3, 1, 0.4, 0.9, 0.3] }}
              transition={{ duration: 0.9, repeat: Infinity, delay: i * 0.12 }} />
          ))}
        </span>
      ) : state === "paused" ? (
        <Play className="h-4 w-4 text-gold" />
      ) : (
        <Volume2 className="h-4 w-4 text-gold transition-transform duration-300 group-hover:scale-110" />
      )}
      <span className="font-body">{ctx ? ctx.t(label) : label}</span>
    </button>
  );
};
